import React, { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LearningHeader, ContentType } from '@atlassian/learning-components';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { User } from 'src/types';
import { getInspectModeData } from 'src/utils/contentful';
import { sendUIEvent } from 'src/utils/analytics';
import { ContentfulSmCourse } from 'src/graphql/queries/GetSmCourse';
import { FEEDBACK_PAGE_FF } from 'src/constants';
import { LearningProgressStatus, mapServerStatusToComponentsStatus } from 'src/services/http/progress';
import { getSlug } from 'src/utils/slug';
import { getCourseUrl, getLessonUrl, withPrefix } from 'src/utils/getUrl';
import { useLessonProgress } from 'src/store/progress/progressStore';
import { navigateToNextItem, getNextItemSlug } from 'src/pages/utils/navigation';

export interface CourseHeaderProps {
  course: ContentfulSmCourse;
  user: User | null | undefined;
  onLoginUser: () => void;
  isLoading: boolean;
  onSave: () => void;
  isCourseSaved: boolean;
}

const FIELD_ID = 'lessonId';

export const OldCourseHeader: React.FC<CourseHeaderProps> = ({
  course,
  user,
  isLoading,
  onLoginUser,
  onSave,
  isCourseSaved,
}) => {
  const [{ progressForLessons, isLoadingProgressForLessons }] = useLessonProgress();
  const navigate = useNavigate();
  const location = useLocation();

  const currentUrl = globalThis?.location?.href;

  const isFeedbackPageAccessible = FeatureGates.checkGate(FEEDBACK_PAGE_FF);

  const courseSlug = getSlug(course);

  const navigateToLesson = useCallback(
    (lessonSlug: string): void => {
      navigate(withPrefix(getLessonUrl({ lessonSlug }), location.pathname.replace(/\/$/, '')));
    },
    [navigate, location],
  );

  const startCourse = useCallback(() => {
    const lessons = course?.lessonsCollection?.items;

    if (!lessons?.length) {
      return;
    }

    navigateToNextItem({
      items: lessons,
      progress: {
        overallStatus: progressForLessons?.overallStatus || LearningProgressStatus.NOT_STARTED,
        lessons: progressForLessons?.lessons || [],
      },
      itemIdField: FIELD_ID,
      contentSlug: courseSlug,
      contentType: ContentType.COURSE,
      userId: user?.account_id,
      navigateToItem: navigateToLesson,
    });
  }, [course, progressForLessons, navigateToLesson, user, courseSlug]);

  const handleSignIn = useCallback((): void => {
    if (onLoginUser) {
      sendUIEvent('courseScreen', 'button', 'clicked', 'signInButton', { objectType: 'courseHeader' });
      onLoginUser();
    }
  }, [onLoginUser]);

  const handleSave = useCallback((): void => {
    if (onSave) {
      onSave();
      sendUIEvent('courseScreen', 'button', 'clicked', 'saveForLaterButton', {
        resourceType: 'course',
        resourceSlug: courseSlug,
      });
    }
  }, [onSave, courseSlug]);

  // Add conditions when the survey is completed the button should not be shown
  const shouldShowFeedback =
    progressForLessons?.overallStatus === LearningProgressStatus.COMPLETED && isFeedbackPageAccessible;
  const navigateToFeedbackPage = useCallback(() => {
    const url = getCourseUrl(courseSlug.replace(/\/$/, ''), '', true);

    window.open(url, '_blank');
  }, [courseSlug]);

  const handleHrefLink = useCallback((): string => {
    const lessons = course?.lessonsCollection?.items;
    const locationPrefix = location.pathname.replace(/\/$/, '');

    if (!lessons?.length) {
      return '';
    }

    const nextLessonSlug = getNextItemSlug({
      items: lessons,
      progress: {
        overallStatus: progressForLessons?.overallStatus || LearningProgressStatus.NOT_STARTED,
        lessons: progressForLessons?.lessons || [],
      },
      itemIdField: FIELD_ID,
      contentType: ContentType.COURSE,
    });

    return withPrefix(`${locationPrefix}${getLessonUrl({ lessonSlug: nextLessonSlug })}`);
  }, [course, progressForLessons, location.pathname]);

  return (
    <div data-testid="course-header">
      <LearningHeader
        inspectMode={getInspectModeData(course ? course : { sys: { id: '' } }, ['title'])}
        anonymousView={!user?.account_id}
        heading={course?.title || 'Course Title Not Found'}
        urlToShare={currentUrl}
        onSignIn={handleSignIn}
        onStartCourse={startCourse}
        isLoading={isLoading || isLoadingProgressForLessons}
        tagLine=""
        contentType={ContentType.COURSE}
        progressStatus={mapServerStatusToComponentsStatus(progressForLessons?.overallStatus)}
        onSave={handleSave}
        isSaved={isCourseSaved}
        shouldShowFeedback={shouldShowFeedback}
        onStartFeedback={navigateToFeedbackPage}
        href={handleHrefLink()}
      />
    </div>
  );
};
